import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiTypes, listReportJobs } from "../../../api/routes";
import { WorkStatus } from "../../../client";
import { createBatcher } from "./batcher";

const reportJobsBatcher = createBatcher(async (reportIds: string[]) => {
  const jobs = await listReportJobs(reportIds);
  const map = new Map<string, apiTypes.ReportJobBatchEntry[]>();
  for (const job of jobs) {
    const existing = map.get(job.report_id) ?? [];
    existing.push(job);
    map.set(job.report_id, existing);
  }
  return map;
}, 50);

export function useReportJobs(
  reportId: string,
  options?: { integrationTypes?: string[] }
) {
  const queryClient = useQueryClient();

  const { data: allJobs = [] } = useQuery({
    queryKey: ["report-jobs", reportId],
    queryFn: () => reportJobsBatcher.load(reportId).then((jobs) => jobs ?? []),
    refetchInterval: 5000,
  });

  const filter = options?.integrationTypes;
  const jobs = useMemo(
    () =>
      filter
        ? allJobs.filter((j) => filter.includes(j.integration_type))
        : allJobs,
    [allJobs, filter]
  );

  const fetchJobs = () =>
    queryClient.invalidateQueries({ queryKey: ["report-jobs", reportId] });

  return { jobs, fetchJobs };
}

export function useQueueJobs(reportIds: string[], statuses: WorkStatus[]) {
  return useQuery({
    queryKey: ["queue-jobs", reportIds],
    queryFn: () => listReportJobs(reportIds, statuses),
    enabled: reportIds.length > 0,
    refetchInterval: 5000,
  });
}
